// src/components/companion/MarlowIntro.jsx
//
// Marlow's introduction. Shown BEFORE the consent gate to anyone who has never
// met her, and re-openable afterwards from the "About Marlow" button on the
// chat header — "who is this and what do I do with them" is a question people
// have on the second visit too, not just the first.
//
// Follows the family pattern (Willow's, then Sofia's, then Clara's): name,
// role, a sample of the actual voice, a plain description of what this is and
// isn't, then a clear way in. Nobody should be asked for their name and
// country before they know who is asking or why.

import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, MessageCircle, Sparkles, ShieldCheck, Compass } from 'lucide-react';
import MarlowAvatar from './MarlowAvatar';

const CAN_HELP = [
  'A moment you keep coming back to but have never managed to tell well',
  'A story that always falls a bit flat: worked out why, and fixed',
  'Something you have to tell on Saturday: a toast, a eulogy, an interview answer',
  'A story you are not sure you should tell at all: calibrated before you do, not after',
  'Learning to draw a story out of someone else without hijacking it with your own',
];

const HOW_TO = [
  {
    title: 'Bring a moment, not a topic',
    body: '“The day my dad’s watch stopped” gets you further than “how do I tell better stories”. It doesn’t have to be dramatic or finished. A moment you keep circling is plenty.',
  },
  {
    title: 'She’ll ask for the detail you skipped',
    body: 'This is her signature move and the whole craft. Most of us narrate in summary, and the story is living in the bit we walked straight past. Expect questions like “What were you holding?”, “What did she actually say?” and “What time was it?”, one at a time, and she waits for each answer.',
  },
  {
    title: 'The story stays yours',
    body: 'Marlow never writes it for you. She builds it with you, in your words, and throws out any line that sounds like her instead of you. What you leave with is something you can actually say out loud to a person. That’s the deliverable, not a transcript.',
  },
  {
    title: 'She’ll ask who’s listening',
    body: 'A job interview, a first date and a hospital bedside take different depths of the same event. Marlow will never push you towards a more exposing story just because it would be a better one. A story you regret telling is a failure even if it was beautifully built.',
  },
];

// The root uses initial={false} rather than an opacity entrance — the fleet's
// framer entrances have a history of stranding elements part-faded.
export default function MarlowIntro({ onContinue, returning = false, hasThread = false }) {
  return (
    <motion.div
      initial={false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="max-w-2xl mx-auto bg-slate-900 border border-white/10 rounded-2xl overflow-hidden"
    >
      <div className="p-6 sm:p-8">
        <div className="flex items-center gap-4 mb-5">
          <MarlowAvatar size={56} />
          <div>
            <p className="text-amber-400 text-sm italic">Your companion here</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Marlow</h1>
          </div>
        </div>

        {/* The actual greeting, so the first thing you meet is the voice. */}
        <p className="text-lg sm:text-xl text-slate-100 italic leading-relaxed mb-5">
          “Hello, I&apos;m Marlow. Tell me something that happened to you. It doesn&apos;t have to
          be dramatic, and it doesn&apos;t have to be finished. A moment you keep coming back to is
          plenty. I&apos;ll help you find the story in it.”
        </p>

        <p className="text-sm sm:text-base text-slate-200 leading-relaxed mb-6">
          The Art of Story teaches you to tell your own true stories well: out loud, to another
          human being. Marlow is the rehearsal partner for that: an AI story editor who won&apos;t
          take “it&apos;s not that interesting, really” for an answer. She listens to a rambling
          anecdote and hears the story trapped inside it. Most lives aren&apos;t boring; a lot of our
          tellings are, and that gap is one you can close.
        </p>

        <div className="bg-slate-950 border border-white/10 rounded-xl p-4 sm:p-5 mb-5">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-amber-400 mb-3">
            <MessageCircle size={16} /> What people bring to Marlow
          </h2>
          <ul className="space-y-2">
            {CAN_HELP.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm sm:text-base text-slate-200">
                <Sparkles size={15} className="flex-shrink-0 mt-1 text-amber-400" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-slate-950 border border-white/10 rounded-xl p-4 sm:p-5 mb-6">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-amber-400 mb-3">
            <Compass size={16} /> How Marlow works
          </h2>
          <ul className="space-y-3">
            {HOW_TO.map((item) => (
              <li key={item.title} className="text-sm sm:text-base">
                <p className="text-slate-100 font-medium">{item.title}</p>
                <p className="text-slate-400 leading-relaxed">{item.body}</p>
              </li>
            ))}
          </ul>
          <p className="text-sm text-slate-400 mt-4 pt-3 border-t border-white/10 leading-relaxed">
            Your conversation stays put if you wander off to try a tool Marlow mentioned. Come back
            and it&apos;ll be where you left it. Start a fresh one whenever you like from the header.
            And when you&apos;ve actually told the story to someone, come back and tell her how it
            went. That is the part she likes best, and it&apos;s the only real test.
          </p>
        </div>

        {/* Said plainly and up front rather than buried in consent small print. */}
        <div className="flex items-start gap-3 text-sm sm:text-base text-slate-400 mb-7">
          <ShieldCheck size={18} className="flex-shrink-0 mt-0.5 text-slate-500" />
          <p>
            Marlow is not a therapist, counsellor or doctor, and storytelling is not therapy. She
            works on true stories from your own life: not fiction, not speeches for other people,
            and never helping an event become something it wasn&apos;t. If real weight arrives, the
            craft gets set down and she says so. What Marlow remembers is stored in Australia. To
            write each reply, your message is sent to Anthropic&apos;s Claude, which doesn&apos;t use it
            to train AI. We never sell your data or share it with advertisers. You can read, export
            or delete everything Marlow keeps, whenever you like.
          </p>
        </div>

        <button
          onClick={onContinue}
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-lg font-semibold text-base transition-colors"
        >
          {returning
            ? (hasThread ? 'Back to our conversation' : 'Back to Marlow')
            : 'Start talking with Marlow'}
          <ArrowRight size={18} />
        </button>
        {!returning && (
          <p className="text-xs text-slate-400 text-center mt-3">
            Free: five messages a day. Marlow will ask a couple of things about you first.
          </p>
        )}
      </div>
    </motion.div>
  );
}
